import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { serviceStats } from '../../../mocks/servicesPage';

export default function ServicesContact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    privacy: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Ensure privacy is accepted before sending
    if (!formData.privacy) return;

    setIsSubmitting(true);
    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6n80885dsf7sr1n6k1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form after a successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          privacy: false,
        });
      } else {
        // Capture server‑side errors for better debugging
        const errorText = await response.text();
        console.error('Form submission failed:', response.status, errorText);
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Network or unexpected error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-services" className="py-16 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="lg:col-span-5 space-y-6">
            {/* Badge */}
            <span className="inline-block px-3 py-1.5 bg-white/60 border border-slate-900/10 text-slate-900/70 text-sm font-semibold uppercase tracking-widest rounded">
              Let's Build Your Success Together
            </span>

            {/* Title */}
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight">
              Ready to<br />Take the<br />Next Step?
            </h2>

            {/* Description */}
            <p className="text-lg text-slate-700 leading-relaxed">
              Whether you're launching, expanding, or streamlining your operations, our experts are
              here to guide you with tailored solutions.
            </p>

            <p className="text-lg text-slate-700">
              <span className="font-semibold">Partner with CJ Solutions</span> and unlock strategic
              growth across regulatory, financial, and corporate services.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6 pt-8">
              {serviceStats.map((stat, index) => (
                <div key={index} className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <div className="text-3xl lg:text-4xl font-bold text-amber-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-10">
              <h3 className="text-2xl lg:text-3xl font-semibold text-slate-900 mb-8">
                Get Expert Advice Today
              </h3>

              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-xl font-semibold text-slate-900 mb-2">Thank You!</h4>
                  <p className="text-slate-600">We'll be in touch with you shortly.</p>
                </div>
              ) : (
                <form
                  id="services-consultation-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Name and surname <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter Your Name"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Email Address"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Phone Number"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {/* Service Selection */}
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-2">
                      Service of Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="iGaming & FX Licensing">iGaming & FX Licensing</option>
                      <option value="AML & KYC Outsourcing">AML & KYC Outsourcing</option>
                      <option value="Compliance Outsourcing">Compliance Outsourcing</option>
                      <option value="Tax & Payroll Outsourcing">Tax & Payroll Outsourcing</option>
                      <option value="Accounting Outsourcing">Accounting Outsourcing</option>
                      <option value="Legal Drafting Outsourcing">Legal Drafting Outsourcing</option>
                      <option value="Company Formation Services">Company Formation Services</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value.slice(0, 500) })
                      }
                      placeholder="Add Your Message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                    ></textarea>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.message.length}/500 characters
                    </p>
                  </div>

                  {/* Privacy Checkbox */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="privacy"
                      checked={formData.privacy}
                      onChange={(e) => setFormData({ ...formData, privacy: e.target.checked })}
                      required
                      className="mt-1 w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500 cursor-pointer"
                    />
                    <label htmlFor="privacy" className="text-sm text-slate-600 cursor-pointer">
                      I agree to the{' '}
                      <Link to="/privacy-policy" className="text-amber-500 underline">
                        Privacy Policy
                      </Link>
                      . <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.privacy}
                    className="w-full py-4 bg-amber-500 text-white text-lg font-semibold rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Submitting...' : 'Book a Free Consultation Today'}
                  </button>

                  {submitStatus === 'error' && (
                    <p className="text-red-600 text-sm text-center">
                      Something went wrong. Please try again.
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
